import { useState, useEffect } from "react";
import { AppNavigation } from "../components/AppNavigation";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { 
  User,
  Plus,
  Trash2,
  Link as LinkIcon,
  Twitter,
  Instagram,
  Facebook,
  Linkedin,
  Youtube,
  CheckCircle,
  AlertCircle,
  ExternalLink
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const SettingsPage = ({ user, logout, token }) => {
  const [socialAccounts, setSocialAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newAccount, setNewAccount] = useState({
    platform: "twitter",
    account_name: "",
    profile_url: ""
  });

  const platformOptions = [
    { id: "twitter", name: "Twitter", icon: <Twitter className="w-5 h-5" />, color: "bg-blue-500" },
    { id: "instagram", name: "Instagram", icon: <Instagram className="w-5 h-5" />, color: "bg-pink-500" },
    { id: "facebook", name: "Facebook", icon: <Facebook className="w-5 h-5" />, color: "bg-indigo-500" },
    { id: "linkedin", name: "LinkedIn", icon: <Linkedin className="w-5 h-5" />, color: "bg-sky-500" },
    { id: "tiktok", name: "TikTok", icon: <span className="font-bold">TT</span>, color: "bg-gray-800" },
    { id: "youtube", name: "YouTube", icon: <Youtube className="w-5 h-5" />, color: "bg-red-500" }
  ];

  useEffect(() => {
    fetchSocialAccounts();
  }, [token]);

  const fetchSocialAccounts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/social`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSocialAccounts(response.data);
    } catch (error) {
      console.error("Error fetching accounts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAccount = async () => {
    if (!newAccount.account_name.trim()) {
      toast.error("El nombre de cuenta es requerido");
      return;
    }

    try {
      await axios.post(
        `${BACKEND_URL}/api/social`,
        newAccount,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Cuenta añadida");
      setAddDialogOpen(false);
      setNewAccount({ platform: "twitter", account_name: "", profile_url: "" });
      fetchSocialAccounts();
    } catch (error) {
      toast.error("Error al añadir cuenta");
    }
  };

  const handleDeleteAccount = async (accountId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/social/${accountId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Cuenta eliminada");
      fetchSocialAccounts();
    } catch (error) {
      toast.error("Error al eliminar cuenta");
    }
  };

  const getPlatformInfo = (platformId) => {
    return platformOptions.find(p => p.id === platformId) || platformOptions[0];
  };

  return (
    <div className="min-h-screen bg-background">
      <AppNavigation user={user} logout={logout} />
      
      <main className="lg:ml-64 pt-14 lg:pt-0 pb-20 lg:pb-0 p-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Configuración</h1>
            <p className="text-muted-foreground mt-1">
              Gestiona tu perfil y cuentas de redes sociales
            </p>
          </div>

          {/* Profile Section */}
          <Card className="mb-6" data-testid="profile-section">
            <CardHeader>
              <CardTitle>Perfil</CardTitle>
              <CardDescription>Tu información personal</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6">
                <Avatar className="w-20 h-20">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="text-2xl bg-primary/10 text-primary">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="space-y-1">
                  <h3 className="text-xl font-semibold">{user?.name}</h3>
                  <p className="text-muted-foreground">{user?.email}</p>
                  <Badge variant="secondary" className="mt-2">Plan Gratuito</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Social Accounts Section */}
          <Card data-testid="social-accounts-section">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Cuentas de Redes Sociales</CardTitle>
                <CardDescription>Conecta tus perfiles para publicar</CardDescription>
              </div>
              <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-account-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Añadir Cuenta
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Añadir Cuenta de Red Social</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Plataforma</Label>
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        {platformOptions.map((platform) => (
                          <button
                            key={platform.id}
                            type="button"
                            className={`p-3 rounded-lg border flex flex-col items-center gap-2 transition-all ${
                              newAccount.platform === platform.id 
                                ? 'border-primary bg-primary/5' 
                                : 'border-border hover:border-primary/50'
                            }`}
                            onClick={() => setNewAccount({ ...newAccount, platform: platform.id })}
                            data-testid={`select-platform-${platform.id}`}
                          >
                            <span className={`p-2 rounded text-white ${platform.color}`}>
                              {platform.icon}
                            </span>
                            <span className="text-sm">{platform.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label>Nombre de Usuario</Label>
                      <Input 
                        placeholder="@username"
                        value={newAccount.account_name}
                        onChange={(e) => setNewAccount({ ...newAccount, account_name: e.target.value })}
                        data-testid="account-name-input"
                      />
                    </div>
                    <div>
                      <Label>URL del Perfil (opcional)</Label>
                      <Input 
                        placeholder="https://twitter.com/username"
                        value={newAccount.profile_url}
                        onChange={(e) => setNewAccount({ ...newAccount, profile_url: e.target.value })}
                        data-testid="profile-url-input"
                      />
                    </div>
                    <Button 
                      className="w-full" 
                      onClick={handleAddAccount}
                      data-testid="save-account-btn"
                    >
                      Añadir Cuenta
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : socialAccounts.length === 0 ? (
                <div className="text-center py-8">
                  <LinkIcon className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground mb-4">
                    No tienes cuentas conectadas
                  </p>
                  <Button variant="outline" onClick={() => setAddDialogOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Añadir tu primera cuenta
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {socialAccounts.map((account) => {
                    const platformInfo = getPlatformInfo(account.platform);
                    return (
                      <div 
                        key={account.account_id}
                        className="flex items-center justify-between p-4 border border-border rounded-lg"
                        data-testid={`account-${account.account_id}`}
                      >
                        <div className="flex items-center gap-4">
                          <span className={`p-2 rounded text-white ${platformInfo.color}`}>
                            {platformInfo.icon}
                          </span>
                          <div>
                            <p className="font-medium">{account.account_name}</p>
                            <p className="text-sm text-muted-foreground capitalize">
                              {platformInfo.name}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {account.connected ? (
                            <Badge variant="secondary" className="bg-green-100 text-green-700">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Conectada
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="bg-yellow-100 text-yellow-700">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Pendiente
                            </Badge>
                          )}
                          {account.profile_url && (
                            <a 
                              href={account.profile_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                            >
                              <Button variant="ghost" size="icon">
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            </a>
                          )}
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDeleteAccount(account.account_id)}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-account-${account.account_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Info Note */}
          <Card className="mt-6 bg-secondary/30">
            <CardContent className="pt-6">
              <div className="flex gap-4">
                <AlertCircle className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                <div className="text-sm text-muted-foreground">
                  <p className="font-medium text-foreground mb-1">Nota sobre la conexión</p>
                  <p>
                    La integración real con APIs de redes sociales requiere configuración adicional 
                    y verificación de cuenta. Por ahora, puedes añadir tus cuentas para organizar 
                    tu contenido y preparar publicaciones.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Danger Zone */}
          <Card className="mt-6 border-destructive/20" data-testid="danger-zone">
            <CardHeader>
              <CardTitle className="text-destructive">Zona de Peligro</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Cerrar sesión</p>
                  <p className="text-sm text-muted-foreground">
                    Cierra tu sesión actual
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  className="border-destructive text-destructive hover:bg-destructive/10"
                  onClick={logout}
                  data-testid="logout-settings-btn"
                >
                  Cerrar Sesión
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};
